<script lang="ts">
import { type PrizeDetails, usePrizesStore } from '@/stores/prizes'
import type { Prize } from '@/types'
import { isInPast, isToday, isTomorrow } from '@/utils/date-time-utils'
import { type PropType, defineComponent } from 'vue'

import HighlightsModal from '../modals/HighlightsModal.vue'
import BaseCard from './new/BaseCard.vue'

/**
 * A card to display a prize.
 *
 */
export default defineComponent({
  components: { BaseCard, HighlightsModal },
  props: {
    prize: {
      type: Object as PropType<Prize>,
      required: true,
    },
  },
  setup() {
    return {
      isInPast,
      prizesStore: usePrizesStore(),
    }
  },
  data() {
    return {
      ticketCount: 0,
    }
  },
  computed: {
    details(): PrizeDetails {
      return this.prizesStore.generateDetails(this.prize)
    },
    header(): Partial<{ text: string; class: string }> {
      if (isToday(this.prize.drawn_at)) {
        return {
          text: 'Ends Today',
          class: 'bg-pastel-blue',
        }
      }

      if (isTomorrow(this.prize.drawn_at)) {
        return {
          text: 'Ends Tomorrow',
          class: 'bg-slate-800',
        }
      }

      return {
        class: 'bg-primary',
      }
    },
    to() {
      if (this.$userStore.isLoggedIn && !this.$userStore.hasSubscription) {
        return {
          name: 'memberships',
        }
      }

      if (this.prize.campaign_code) {
        return {
          name: 'campaign',
          params: { campaignCode: this.prize.campaign_code },
        }
      }

      return {
        name: 'prize',
        params: { prizeCode: this.prize.code },
      }
    },
  },
  mounted() {
    this.ticketCount =
      this.$userStore.ticketCount +
      this.$userStore.getDrawTicketCount(this.prize.code)
  },
  methods: {
    goTo(event: MouseEvent, openModal: () => void) {
      if (this.$userStore.isLoggedIn) {
        this.openDetailsModal(event, openModal)
      } else {
        void this.$router.push(this.to)
      }
    },
    openDetailsModal(event: MouseEvent, openModal: () => void) {
      event.stopImmediatePropagation()
      openModal()
    },
  },
})
</script>

<template>
  <HighlightsModal :id="`highlights-modal-${prize.code}`" :prize="prize">
    <template #button="{ openModal }">
      <BaseCard
        class="gap-0 p-0 hover:cursor-pointer"
        @click="(e: MouseEvent) => goTo(e, openModal)"
      >
        <img
          :src="prize.card_image.src"
          :alt="prize.card_image.alt"
          class="object-cover"
        />

        <div
          class="flex items-center gap-3 bg-primary p-1 prize-countdown-bar justify-center"
        >
          <AppCountdown
            :datetime="prize.drawn_at"
            time-class="font-semibold text-pure-white leading-5"
            unit-class="font-semibold text-pure-white leading-5"
          >
            <template #default="{ countdown }">
              <p
                class="font-semibold leading-5 text-pure-white w-[79px] text-right whitespace-nowrap"
                :class="{ 'flex flex-col': countdown.showAsDate }"
              >
                <template v-if="countdown.showAsDate">Draw date:</template>
                <template v-if="!countdown.showAsDate">
                  <template v-if="isInPast(prize.close_at)">
                    Drawn at:
                  </template>
                  <template v-if="!isInPast(prize.close_at)">
                    Ends in:
                  </template>
                </template>
              </p>
            </template>
          </AppCountdown>
        </div>

        <div class="flex h-full w-full flex-col justify-between p-3">
          <div
            class="font-bold line-clamp-1 leading-5 md:text-xl md:leading-6 mb-1"
          >
            {{ prize.title }}
          </div>
          <template v-if="$userStore.isLoggedOut">
            <p
              class="flex w-fit items-center border-b border-b-black hover:border-b-transparent"
              @click="(e: MouseEvent) => openDetailsModal(e, openModal)"
            >
              See Full Details
              <AppIcon :icon="Icons.ArrowLeft" class="-rotate-180" size="2xs" />
            </p>

            <AppButton
              :route="to"
              :disabled="isInPast(prize.close_at)"
              :tooltip="{
                content: isInPast(prize.close_at)
                  ? `Entry for the ${prize.title} draw has closed`
                  : `Enter the ${prize.title} draw for free`,
                placement: 'bottom',
              }"
              class="button-primary mt-6 whitespace-nowrap !py-2 !px-4 text-sm leading-4 !font-bold !md:px-6 !md:py-3 md:text-base md:leading-5"
              :icon="Icons.ArrowRight"
            >
              {{ isInPast(prize.close_at) ? 'Draw closed' : 'Enter for free' }}
            </AppButton>
          </template>

          <template v-if="$userStore.isLoggedIn">
            <p
              class="flex w-fit items-center border-b border-b-black hover:border-b-transparent"
              @click="(e: MouseEvent) => openDetailsModal(e, openModal)"
            >
              See full details
              <AppIcon :icon="Icons.ArrowLeft" class="-rotate-180" size="2xs" />
            </p>

            <p v-if="ticketCount > 0" class="mt-6 font-bold">
              You have {{ ticketCount }} Entries in draw
            </p>

            <AppButton
              v-if="!$userStore.hasSubscription"
              :route="to"
              :disabled="isInPast(prize.close_at)"
              :tooltip="{
                content: isInPast(prize.close_at)
                  ? `Entry for the ${prize.title} draw has closed`
                  : `Check out our memberships now!`,
                placement: 'bottom',
              }"
              class="button-primary mt-6 whitespace-nowrap !py-2 !px-4 text-sm leading-4 !font-bold !md:px-6 !md:py-3 md:text-base md:leading-5"
              :icon="Icons.ArrowRight"
            >
              {{
                isInPast(prize.close_at) ? 'Draw closed' : '7 Day Free Trial'
              }}
            </AppButton>
          </template>
        </div>
      </BaseCard>
    </template>
  </HighlightsModal>
</template>

<style lang="scss" scoped>
.prize-countdown-bar :deep(time) {
  @apply w-[105px];
}
</style>
